import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../common/state/store";
import { useForm } from "react-hook-form";
import AccessTypeTable from "../../features/projects/components/AccessTypeTable";
import type { INewProjectGroup } from "../../features/projectGroups/state/projectGroupsSlice";
import {
  getNewProjectGroup,
  postProjectGroup,
  setCurrentElements,
  setNewProjectGroup,
  fetchAllCurrentElements,
  setSelectedProjectGroupFromProjectGroupId,
  fetchSpecificProjectGroupWithProjects,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import { getCurrentOrganization, getCurrentUser } from "../../features/profile/state/profileSlice";
import { FooterButtons } from "../../common/components/FooterButtons";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import { duplicateProject } from "../../features/projects/state/projectsSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { LoadingState, setIsLoading } from "src/common/state/slice/appStateSlice";
interface ITemplateProjectAccess {
  decrementStep: () => void;
}

interface LocationState {
  cloneProject?: boolean;
}

export const PROJECT_GROUP_CREATE = "projectGroupCreate";

const TemplateProjectAccess = ({ decrementStep }: ITemplateProjectAccess) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation() as { state?: LocationState };
  const currentUser = useAppSelector(getCurrentUser);
  const currentOrg = useAppSelector(getCurrentOrganization);
  const lastClonedProjectId = useAppSelector(
    (state: RootState) => state.projects.lastClonedProjectId,
  );
  const lastClonedProjectName = useAppSelector(
    (state: RootState) => state.projects.lastClonedProjectName,
  );
  const newProjectGroup = useAppSelector(getNewProjectGroup);
  const { handleSubmit, setValue, watch } = useForm<INewProjectGroup>();
  const cloneProject = location.state?.cloneProject || false;

  const onSubmit = (data: INewProjectGroup) => {
    if (!currentOrg) return;

    void dispatch(
      postProjectGroup({
        name: newProjectGroup.newName,
        source_thumbnail: newProjectGroup.thumbnailId,
        organization: Number(currentOrg.id),
        auth_type: data?.accessType ? data.accessType : "classic",
        componentId: PROJECT_GROUP_CREATE,
        theme_info: newProjectGroup.themeId,
        created_by: currentUser?.email ? currentUser.email : "",
        source_app_main_menu_media: Number(newProjectGroup.menuMediaId),
      }),
    ).then((res) => {
      if (!postProjectGroup.fulfilled.match(res)) {
        console.error("Error in validateAndSubmit:", res.error);
        navigate("/");
        dispatch(
          setAlert({
            msg: t("error.errorWhileCreatingProject"),
            type: "error",
            isOpen: true,
            showCloseButton: true,
          }),
        );
        return;
      }

      const projectGroupId = res.payload.data.id;

      dispatch(setSelectedProjectGroupFromProjectGroupId(projectGroupId));

      if (cloneProject && lastClonedProjectId) {
        dispatch(
          duplicateProject({
            project: lastClonedProjectId,
            thumbnail: 47,
            projectGroup: projectGroupId,
            newName: lastClonedProjectName ?? "",
            enabledLanguagesIds: ["1"],
            componentId: PROJECT_GROUP_CREATE,
          }),
        )
          .then(async () => {
            dispatch(
              setIsLoading({ componentId: PROJECT_GROUP_CREATE, loading: LoadingState.Loading }),
            );
            dispatch(setSelectedProjectGroupFromProjectGroupId(projectGroupId));
            const results = await Promise.allSettled([
              dispatch(fetchSpecificProjectGroupWithProjects({ projectGroupId })),
              dispatch(fetchAllCurrentElements({ projectGroupId })),
            ]);

            results.forEach((result, index) => {
              if (result.status === "rejected") {
                const operationNames = [
                  "fetchSpecificProjectGroupWithProjects",
                  "fetchAllCurrentElements",
                ];
                console.error(`Failed to execute ${operationNames[index]}:`, result.reason);
              }
            });
            dispatch(
              setIsLoading({ componentId: PROJECT_GROUP_CREATE, loading: LoadingState.Success }),
            );

            navigate(`/courses/${projectGroupId}/list`);

            dispatch(
              setAlert({
                msg: t(
                  "chooseTemplateType.createCourseFromTemplateModel.projectGeneratedSuccessfully",
                ),
                type: "success",
                isOpen: true,
                showCloseButton: true,
                customTimeoutInMs: 10000,
              }),
            );
          })
          .catch((error) => {
            console.error("Error while cloning the project", error);
            navigate("/");
            dispatch(
              setAlert({
                msg: t("error.errorWhileCreatingProject"),
                type: "error",
                isOpen: true,
                showCloseButton: true,
              }),
            );
          });
      } else {
        navigate(`/courses/${projectGroupId}/list`);
      }

      dispatch(
        setNewProjectGroup({
          accessType: "classic",
          newName: "",
          thumbnailId: 47,
          themeId: 1,
          menuMediaId: 5346,
        }),
      );

      dispatch(setCurrentElements([]));
    });
  };

  return (
    <>
      <IsLoading componentId={PROJECT_GROUP_CREATE} showSuccess={false} spinnerPlaceholder>
        <h1 className="text-2xl font-medium w-full max-w-7xl mx-auto mt-6 mb-4 px-6 xl:px-3">
          {t("chooseTemplateType.accessType")} :
        </h1>
        <form
          className="flex flex-col grow w-full h-full justify-between items-center overflow-y-auto personalize-scroll-visible"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="px-6 xl:px-3">
            <AccessTypeTable setValue={setValue} watch={watch} />
          </div>

          <FooterButtons
            cancelText={t("general.cancel")}
            cancelCb={() => navigate("/courses/")}
            backText={t("general.back")}
            backCb={() => decrementStep()}
            nextText={t("general.create")}
          />
        </form>
      </IsLoading>
    </>
  );
};

export default TemplateProjectAccess;
